import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Plus, FileText, ExternalLink, Trash2, Pencil, Link, FolderOpen, Upload, UserCheck, Building2, ChevronDown, ChevronRight, Filter } from 'lucide-react';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';

const DOC_TYPES = [
  { value: 'drawing', label: 'Drawing / Plan' },
  { value: '3d_render', label: '3D Render' },
  { value: 'contract', label: 'Contract' },
  { value: 'agreement', label: 'Agreement' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'photo', label: 'Site Photo' },
  { value: 'specification', label: 'Specification' },
  { value: 'other', label: 'Other' },
];

const EMPTY_FORM = {
  project_id: '',
  name: '',
  url: '',
  doc_type: 'drawing',
  source: 'link',
  visible_to_client: false,
  visible_to_vendor: false,
};

export default function Documents() {
  const location = useLocation();
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [uploadType, setUploadType] = useState('link');
  const [expandedProjects, setExpandedProjects] = useState({});
  const [filterProject, setFilterProject] = useState('all');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['owner', 'admin', 'staff'].includes(user.role);

  // Determine context from route path
  const isClientView = location.pathname.startsWith('/client/');
  const isVendorView = location.pathname.startsWith('/vendor/');
  const viewContext = isClientView ? 'client' : isVendorView ? 'vendor' : 'all';

  const pageTitle = isClientView ? 'Client Documents' : isVendorView ? 'Vendor Documents' : 'Documents';
  const pageSubtitle = isClientView
    ? 'Documents shared with clients'
    : isVendorView
    ? 'Documents shared with vendors'
    : 'Manage project documents, drawings, and files';

  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [editFormData, setEditFormData] = useState({ ...EMPTY_FORM });

  useEffect(() => {
    fetchDocuments();
    fetchProjects();
  }, [location.pathname]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/documents`);
      let docs = response.data;

      // Client-side filtering based on route context
      if (isClientView) {
        docs = docs.filter(d => d.visible_to_client);
      } else if (isVendorView) {
        docs = docs.filter(d => d.visible_to_vendor);
      }

      setDocuments(docs);
      const expanded = {};
      docs.forEach(doc => {
        if (doc.project_id) expanded[doc.project_id] = true;
      });
      setExpandedProjects(expanded);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.url) {
      toast.error('Please fill in document name and URL');
      return;
    }
    try {
      if (formData.project_id) {
        await axios.post(`${API}/projects/${formData.project_id}/documents`, {
          name: formData.name,
          url: formData.url,
          doc_type: formData.doc_type,
          source: uploadType,
          visible_to_client: formData.visible_to_client,
          visible_to_vendor: formData.visible_to_vendor,
        });
      } else {
        await axios.post(`${API}/documents`, { ...formData, source: uploadType });
      }
      toast.success('Document added successfully');
      setDialogOpen(false);
      fetchDocuments();
      setFormData({ ...EMPTY_FORM });
      setUploadType('link');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add document');
    }
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setEditFormData({
      project_id: doc.project_id || '',
      name: doc.name || '',
      url: doc.url || '',
      doc_type: doc.doc_type || 'other',
      source: doc.source || 'link',
      visible_to_client: doc.visible_to_client || false,
      visible_to_vendor: doc.visible_to_vendor || false,
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingDoc || !editFormData.name || !editFormData.url) {
      toast.error('Please fill in document name and URL');
      return;
    }
    try {
      await axios.put(`${API}/documents/${editingDoc.id}`, {
        name: editFormData.name,
        url: editFormData.url,
        doc_type: editFormData.doc_type,
        source: editFormData.source,
        visible_to_client: editFormData.visible_to_client,
        visible_to_vendor: editFormData.visible_to_vendor,
      });
      toast.success('Document updated');
      setEditDialogOpen(false);
      setEditingDoc(null);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update document');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  // Group documents by project
  const groupedDocuments = documents.reduce((acc, doc) => {
    const projectId = doc.project_id || 'unassigned';
    if (!acc[projectId]) acc[projectId] = [];
    acc[projectId].push(doc);
    return acc;
  }, {});

  const filteredGroups = filterProject === 'all'
    ? groupedDocuments
    : { [filterProject]: groupedDocuments[filterProject] || [] };

  if (loading) {
    return <Layout><div className="text-center py-12" data-testid="loading-state">Loading...</div></Layout>;
  }

  const renderDocumentForm = (data, setData, onSubmit, isEdit = false) => (
    <form onSubmit={onSubmit} className="space-y-4">
      {!isEdit && (
        <div className="grid grid-cols-4 gap-2">
          {[
            { key: 'link', icon: Link, label: 'URL Link' },
            { key: 'gdrive', icon: FolderOpen, label: 'G-Drive' },
            { key: 'onedrive', icon: FolderOpen, label: 'OneDrive' },
          ].map(({ key, icon: Icon, label }) => (
            <Button key={key} type="button" variant={uploadType === key ? 'default' : 'outline'}
              onClick={() => setUploadType(key)} className="flex flex-col items-center p-3 h-auto">
              <Icon size={20} /><span className="text-xs mt-1">{label}</span>
            </Button>
          ))}
          <Button type="button" variant="outline" disabled className="flex flex-col items-center p-3 h-auto opacity-50">
            <Upload size={20} /><span className="text-xs mt-1">Upload</span>
          </Button>
        </div>
      )}

      {!isEdit && uploadType === 'gdrive' && (
        <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
          <p className="font-medium mb-1">How to get Google Drive link:</p>
          <ol className="list-decimal list-inside space-y-1 text-xs">
            <li>Open your file in Google Drive</li>
            <li>Click "Share" &rarr; "Anyone with the link"</li>
            <li>Copy and paste the link below</li>
          </ol>
        </div>
      )}

      {!isEdit && uploadType === 'onedrive' && (
        <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
          <p className="font-medium mb-1">How to get OneDrive link:</p>
          <ol className="list-decimal list-inside space-y-1 text-xs">
            <li>Open your file in OneDrive</li>
            <li>Click "Share" &rarr; "Copy link"</li>
            <li>Paste the link below</li>
          </ol>
        </div>
      )}

      {!isEdit && (
        <div>
          <Label>Project *</Label>
          <Select value={data.project_id} onValueChange={(value) => setData({ ...data, project_id: value })}>
            <SelectTrigger data-testid="project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>
              {[...projects]
                .filter(p => p.is_active !== false)
                .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
                .map((p, idx) => <SelectItem key={p.id} value={p.id}>{idx + 1}. {p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label>Document Name *</Label>
        <Input required value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })}
          placeholder="e.g., Floor Plan - Ground Floor" data-testid="document-name-input" />
      </div>

      <div>
        <Label>Document URL *</Label>
        <Input type="url" required value={data.url} onChange={(e) => setData({ ...data, url: e.target.value })}
          placeholder="https://..." data-testid="document-url-input" />
      </div>

      <div>
        <Label>Document Type</Label>
        <Select value={data.doc_type} onValueChange={(value) => setData({ ...data, doc_type: value })}>
          <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Visibility Options */}
      <div className="bg-stone-50 p-3 rounded-lg space-y-3">
        <p className="text-sm font-medium text-stone-700">Visible to:</p>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Checkbox id={`${isEdit ? 'edit_' : ''}visible_to_client`}
              checked={data.visible_to_client}
              onCheckedChange={(checked) => setData({ ...data, visible_to_client: checked })} />
            <Label htmlFor={`${isEdit ? 'edit_' : ''}visible_to_client`} className="flex items-center gap-1 text-sm cursor-pointer">
              <UserCheck size={16} className="text-blue-600" /> Client
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id={`${isEdit ? 'edit_' : ''}visible_to_vendor`}
              checked={data.visible_to_vendor}
              onCheckedChange={(checked) => setData({ ...data, visible_to_vendor: checked })} />
            <Label htmlFor={`${isEdit ? 'edit_' : ''}visible_to_vendor`} className="flex items-center gap-1 text-sm cursor-pointer">
              <Building2 size={16} className="text-amber-600" /> Vendor
            </Label>
          </div>
        </div>
        <p className="text-xs text-stone-500">Only selected roles can see this document in their portal</p>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => isEdit ? setEditDialogOpen(false) : setDialogOpen(false)}>Cancel</Button>
        <Button type="submit" data-testid={isEdit ? "update-document-button" : "submit-document-button"}>
          {isEdit ? 'Update Document' : 'Add Document'}
        </Button>
      </DialogFooter>
    </form>
  );

  return (
    <Layout>
      <div data-testid="documents-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="font-playfair text-4xl font-bold text-stone-900 mb-2" data-testid="documents-title">{pageTitle}</h1>
            <p className="text-stone-600">{pageSubtitle}</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={filterProject} onValueChange={setFilterProject}>
              <SelectTrigger className="w-48">
                <Filter size={16} className="mr-2" />
                <SelectValue placeholder="Filter by project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {[...projects]
                .filter(p => p.is_active !== false)
                .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
                .map((p, idx) => <SelectItem key={p.id} value={p.id}>{idx + 1}. {p.name}</SelectItem>)}
              </SelectContent>
            </Select>

            {isAdmin && (
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (open) {
                  // Pre-set visibility based on route context
                  setFormData(prev => ({
                    ...prev,
                    visible_to_client: isClientView ? true : prev.visible_to_client,
                    visible_to_vendor: isVendorView ? true : prev.visible_to_vendor,
                  }));
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2" data-testid="add-document-button">
                    <Plus size={20} /> Add Document
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg" data-testid="add-document-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-playfair text-2xl">Add New Document</DialogTitle>
                  </DialogHeader>
                  {renderDocumentForm(formData, setFormData, handleSubmit)}
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Documents by Project */}
        {documents.length === 0 ? (
          <div className="text-center py-16 bg-white border border-stone-200 rounded-lg" data-testid="no-documents">
            <FolderOpen size={48} className="mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500 mb-2">No documents uploaded yet</p>
            <p className="text-sm text-stone-400">Add drawings, contracts, and other project documents</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="documents-grouped">
            {Object.entries(filteredGroups).map(([projectId, docs]) => {
              if (!docs || docs.length === 0) return null;
              const project = projects.find(p => p.id === projectId);
              const isExpanded = expandedProjects[projectId] !== false;

              return (
                <div key={projectId} className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                  <button onClick={() => toggleProject(projectId)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-stone-50 transition-colors">
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                      <span className="font-semibold text-stone-900">{project?.name || 'Unassigned Documents'}</span>
                      <Badge variant="outline">{docs.length} documents</Badge>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 border-t border-stone-100 pt-4">
                      {docs.map((doc) => (
                        <div key={doc.id} className="bg-stone-50 border border-stone-200 p-4 rounded-lg hover:shadow-md transition-shadow"
                          data-testid={`document-card-${doc.id}`}>
                          <div className="flex items-start justify-between mb-3">
                            <FileText size={24} className="text-stone-700" />
                            <div className="flex items-center gap-1.5">
                              <Badge variant="outline" className="text-xs">
                                {doc.source === 'gdrive' ? 'G-Drive' : doc.source === 'onedrive' ? 'OneDrive' : 'Link'}
                              </Badge>
                              {isAdmin && (
                                <>
                                  <button onClick={() => handleEdit(doc)}
                                    className="p-1 text-stone-400 hover:text-blue-600 rounded" data-testid={`edit-doc-${doc.id}`}>
                                    <Pencil size={14} />
                                  </button>
                                  <button onClick={() => handleDelete(doc.id)}
                                    className="p-1 text-red-400 hover:text-red-600 rounded" data-testid={`delete-doc-${doc.id}`}>
                                    <Trash2 size={14} />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          <h3 className="font-semibold text-stone-900 mb-1 line-clamp-1" data-testid="document-name">{doc.name}</h3>
                          <p className="text-xs text-stone-500 capitalize mb-2">{doc.doc_type?.replace('_', ' ')}</p>

                          {/* Visibility badges */}
                          <div className="flex items-center gap-1 mb-3">
                            {doc.visible_to_client && (
                              <Badge className="text-xs bg-blue-100 text-blue-700">
                                <UserCheck size={10} className="mr-1" /> Client
                              </Badge>
                            )}
                            {doc.visible_to_vendor && (
                              <Badge className="text-xs bg-amber-100 text-amber-700">
                                <Building2 size={10} className="mr-1" /> Vendor
                              </Badge>
                            )}
                          </div>

                          <a href={doc.url} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700" data-testid="document-link">
                            <ExternalLink size={14} /> Open Document
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Edit Document Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setEditingDoc(null); }}>
          <DialogContent className="max-w-lg" data-testid="edit-document-dialog">
            <DialogHeader>
              <DialogTitle className="font-playfair text-2xl">Edit Document</DialogTitle>
            </DialogHeader>
            {renderDocumentForm(editFormData, setEditFormData, handleEditSubmit, true)}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
